import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const ViatorExperienceContext = createContext();

export const useViatorExperiences = () => {
  const context = useContext(ViatorExperienceContext);
  if (!context) {
    throw new Error('useViatorExperiences must be used within a ViatorExperienceProvider');
  }
  return context;
};

export const ViatorExperienceProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

  const getBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/viator-experiences/api/v1/viator-experiences`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/viator-experiences/api/v1/viator-experiences`;
    }
    throw new Error('Invalid login type for viator experiences access');
  }, [API_BASE_URL, loginType]);

  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can manage viator experiences');
    }
  }, [loginType]);

  const handleApiResponse = async (response) => {
    let data = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }
    }
    if (!response.ok) {
      const msg = (data && (data.error || data.message)) || 'Request failed';
      const err = new Error(msg);
      err.status = response.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  const clearError = useCallback(() => setError(null), []);

  const fetchExperiences = useCallback(async (options = {}) => {
    validateAccess();
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (options.search) params.append('search', options.search);
      if (options.city) params.append('city', options.city);
      if (options.category) params.append('category', options.category);
      if (options.active !== undefined && options.active !== null) params.append('active', options.active);
      if (options.featured !== undefined && options.featured !== null) params.append('featured', options.featured);
      if (options.limit) params.append('limit', options.limit);
      if (options.offset !== undefined) params.append('offset', options.offset);

      const baseUrl = getBaseUrl();
      const fullUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
      const response = await makeAuthenticatedRequest(fullUrl);
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to fetch experiences';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  const getExperience = useCallback(async (id) => {
    validateAccess();
    if (!id) throw new Error('Experience ID is required');
    setLoading(true);
    setError(null);
    try {
      const response = await makeAuthenticatedRequest(`${getBaseUrl()}/${encodeURIComponent(id)}`);
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to fetch experience';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  const createExperience = useCallback(async (payload) => {
    validateAccess();
    if (!payload || typeof payload !== 'object') throw new Error('Payload is required');
    setLoading(true);
    setError(null);
    try {
      const response = await makeAuthenticatedRequest(getBaseUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to create experience';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  const updateExperience = useCallback(async (id, payload) => {
    validateAccess();
    if (!id) throw new Error('Experience ID is required');
    if (!payload || typeof payload !== 'object') throw new Error('Payload is required');
    setLoading(true);
    setError(null);
    try {
      const response = await makeAuthenticatedRequest(`${getBaseUrl()}/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to update experience';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  const deleteExperience = useCallback(async (id) => {
    validateAccess();
    if (!id) throw new Error('Experience ID is required');
    setLoading(true);
    setError(null);
    try {
      const response = await makeAuthenticatedRequest(`${getBaseUrl()}/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to delete experience';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // =============================================================================
  // DB-DRIVEN UNAPPROVED QUEUE (NEW — reads from unapproved_viator_experiences)
  // =============================================================================

  const fetchPending = useCallback(async (options = {}) => {
    validateAccess();
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (options.includeResolved) params.append('include_resolved', 'true');
      const baseUrl = `${getBaseUrl()}/unapproved`;
      const fullUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
      const response = await makeAuthenticatedRequest(fullUrl);
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to fetch unapproved experiences';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  const approvePending = useCallback(async (productCode, overrides = null) => {
    validateAccess();
    if (!productCode) throw new Error('product_code is required');
    setLoading(true);
    setError(null);
    try {
      const body = { product_code: productCode };
      if (overrides) body.experience = overrides;
      const response = await makeAuthenticatedRequest(`${getBaseUrl()}/unapproved/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to approve experience';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  const approvePendingBulk = useCallback(async (productCodes = []) => {
    validateAccess();
    if (!Array.isArray(productCodes) || productCodes.length === 0) {
      throw new Error('product_codes is required');
    }
    setLoading(true);
    setError(null);
    try {
      const response = await makeAuthenticatedRequest(`${getBaseUrl()}/unapproved/approve-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_codes: productCodes }),
      });
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to approve experiences';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  const rejectPending = useCallback(async (productCode) => {
    validateAccess();
    if (!productCode) throw new Error('product_code is required');
    setLoading(true);
    setError(null);
    try {
      const response = await makeAuthenticatedRequest(
        `${getBaseUrl()}/unapproved/${encodeURIComponent(productCode)}/reject`,
        { method: 'POST' },
      );
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to reject experience';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // =============================================================================
  // FILE-BASED PENDING QUEUE (legacy — JSON scrape files)
  // =============================================================================

  const fetchPendingLegacy = useCallback(async (options = {}) => {
    validateAccess();
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (options.includeResolved) params.append('include_resolved', 'true');
      const baseUrl = `${getBaseUrl()}/pending`;
      const fullUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
      const response = await makeAuthenticatedRequest(fullUrl);
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to fetch pending experiences';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // =============================================================================
  // SCRAPE QUEUE (scraped_viator_indexes table monitoring)
  // =============================================================================

  const fetchScrapeQueueStats = useCallback(async () => {
    validateAccess();
    setLoading(true);
    setError(null);
    try {
      const response = await makeAuthenticatedRequest(`${getBaseUrl()}/scrape-queue/stats`);
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to fetch scrape queue stats';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  const fetchScrapeQueue = useCallback(async (options = {}) => {
    validateAccess();
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (options.scraped !== undefined && options.scraped !== null) {
        params.append('scraped', options.scraped ? 'true' : 'false');
      }
      if (options.limit) params.append('limit', String(options.limit));
      if (options.offset !== undefined) params.append('offset', String(options.offset));

      const baseUrl = `${getBaseUrl()}/scrape-queue`;
      const fullUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
      const response = await makeAuthenticatedRequest(fullUrl);
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to fetch scrape queue';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // DELETE /api/v1/viator-experiences/scrape-queue/{id}
  const deleteScrapeQueueEntry = useCallback(async (id) => {
    validateAccess();
    if (!id) throw new Error('Queue entry ID is required');
    setLoading(true);
    setError(null);
    try {
      const response = await makeAuthenticatedRequest(
        `${getBaseUrl()}/scrape-queue/${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      );
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to delete queue entry';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  // DELETE /api/v1/viator-experiences/scrape-queue
  const clearScrapeQueue = useCallback(async (scrapedFilter = null) => {
    validateAccess();
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (scrapedFilter !== null) params.append('scraped', scrapedFilter ? 'true' : 'false');

      const baseUrl = `${getBaseUrl()}/scrape-queue`;
      const fullUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
      const response = await makeAuthenticatedRequest(fullUrl, { method: 'DELETE' });
      return await handleApiResponse(response);
    } catch (err) {
      const msg = err.message || 'Failed to clear scrape queue';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBaseUrl]);

  const value = {
    loading,
    error,
    clearError,
    fetchExperiences,
    getExperience,
    createExperience,
    updateExperience,
    deleteExperience,
    fetchPending,
    approvePending,
    approvePendingBulk,
    rejectPending,
    fetchPendingLegacy,
    fetchScrapeQueueStats,
    fetchScrapeQueue,
    deleteScrapeQueueEntry,
    clearScrapeQueue,
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasViatorAccess: loginType === 'admin' || loginType === 'master',
  };

  return (
    <ViatorExperienceContext.Provider value={value}>
      {children}
    </ViatorExperienceContext.Provider>
  );
};
